import type { ScoringSex } from './types';

/**
 * Pull-up scoring uses its own model because body weight is part of the
 * resistance. Applying the normal DOTS body-weight correction on top would
 * double-normalize body weight, so DOTS is deliberately NOT used here.
 *
 * 100 points represents a strong but attainable recreational performance,
 * comparable in difficulty to the other categories' 100-point standards.
 *
 * Tuning knobs: the two tables below. Each entry is
 * [bodyWeightLb, strictBodyweightRepsFor100Points].
 */
export const PULLUP_BENCHMARK_REPS: Record<ScoringSex, readonly (readonly [number, number])[]> = {
  // Primary reference: 200 lb male x 8 strict pull-ups = 100 points.
  male: [
    [120, 10],
    [140, 10],
    [160, 9],
    [180, 9],
    [200, 8],
    [220, 8],
    [240, 7],
    [260, 7],
  ],
  // Primary reference: 140 lb female x 6 strict pull-ups = 100 points.
  female: [
    [120, 6],
    [140, 6],
    [160, 5],
    [180, 5],
    [200, 4],
    [220, 4],
    [240, 3],
    [260, 3],
  ],
} as const;

/** Epley factor. Applied uniformly, including at 1 rep. */
export function epleyFactor(reps: number): number {
  return 1 + reps / 30;
}

/**
 * Reps at body weight that equal 100 points for this athlete, linearly
 * interpolated between table anchors. Body weights outside the table clamp to
 * the nearest endpoint rather than extrapolating.
 */
export function benchmarkRepsForBodyWeight(bodyWeightLb: number, sex: ScoringSex): number {
  const table = PULLUP_BENCHMARK_REPS[sex];
  const first = table[0];
  const last = table[table.length - 1];

  if (bodyWeightLb <= first[0]) return first[1];
  if (bodyWeightLb >= last[0]) return last[1];

  for (let i = 0; i < table.length - 1; i++) {
    const [lowBw, lowReps] = table[i];
    const [highBw, highReps] = table[i + 1];
    if (bodyWeightLb >= lowBw && bodyWeightLb <= highBw) {
      const t = (bodyWeightLb - lowBw) / (highBw - lowBw);
      return lowReps + (highReps - lowReps) * t;
    }
  }

  return last[1];
}

/** The estimated 1RM that equals 100 points: body weight at the benchmark reps. */
export function pullupBenchmarkE1RmLb(bodyWeightLb: number, sex: ScoringSex): number {
  return bodyWeightLb * epleyFactor(benchmarkRepsForBodyWeight(bodyWeightLb, sex));
}

/**
 * Estimated 1RM for a pull-up set. The effective load is the athlete's full body
 * weight plus any added weight - never the added weight alone.
 */
export function pullupE1RmLb(bodyWeightLb: number, addedWeightLb: number, reps: number): number {
  return (bodyWeightLb + addedWeightLb) * epleyFactor(reps);
}

/**
 * Points for a pull-up set. Not capped at 100 - weighted pull-ups are the
 * natural progression above it.
 */
export function calculatePullupPoints(params: {
  bodyWeightLb: number;
  addedWeightLb: number;
  reps: number;
  sex: ScoringSex;
  /** Per-exercise difficulty factor (pull-up 1.0, chin-up slightly lower). */
  difficultyMultiplier?: number;
}): number {
  const e1rm =
    pullupE1RmLb(params.bodyWeightLb, params.addedWeightLb, params.reps) *
    (params.difficultyMultiplier ?? 1);
  return (100 * e1rm) / pullupBenchmarkE1RmLb(params.bodyWeightLb, params.sex);
}
